import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Input,
  Spinner,
} from 'reactstrap';
import { FaPlus, FaTrash, FaCheck } from 'react-icons/fa';
import i18n from '@/utils/i18n';
import { TrackableItem } from '../../../../graphql/generatedTypes';
import { useTrackableItemQuery } from '../../../../graphql/queries/trackableItem.generated';
import { useUpdateOrderFulfillmentMutation } from '../../../../graphql/mutations/updateOrderFulfillment.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';
import TrackableItemDisplay from '../../../../components/TrackableItem/TrackableItemDisplay';

// Type for the trackable items that come from OrderFulfillment query (partial data)
type OrderFulfillmentTrackableItem = {
  __typename?: 'TrackableItem';
  id: string;
  name: string;
  epc_tag: string;
  item_type: {
    __typename?: 'ItemType';
    id: number;
    name: string;
    image_url?: string | null;
    color_hex_code?: string | null;
    category: string;
  };
};

// ItemType for full TrackableItem (from validation query)
type ItemTypeWithAllFields = {
  __typename?: 'ItemType';
  id: number;
  name: string;
  image_url?: string | null;
  color_hex_code?: string | null;
  category: string;
  archived: boolean;
  epc_asset_id: number;
};

interface TrackableItemsTableProps {
  orderFulfillmentId: string;
  trackableItems: OrderFulfillmentTrackableItem[];
  refetchOrderFulfillment: () => void;
  allowDelete?: boolean;
  allowAdd?: boolean;
}

const TrackableItemsTable: React.FC<TrackableItemsTableProps> = ({
  orderFulfillmentId,
  trackableItems,
  refetchOrderFulfillment,
  allowDelete = true,
  allowAdd = true,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] =
    useState<OrderFulfillmentTrackableItem | null>(null);
  const [newItemId, setNewItemId] = useState('');
  const [validatingItem, setValidatingItem] = useState(false);
  const [validatedItem, setValidatedItem] = useState<TrackableItem | null>(
    null,
  );
  const [showConfirmAddModal, setShowConfirmAddModal] = useState(false);

  const [updateOrderFulfillment, { loading: updatingOrderFulfillment }] =
    useUpdateOrderFulfillmentMutation({
      onCompleted: () => {
        refetchOrderFulfillment();
        LayoutStoreInstance.setSuccess(i18n.t('TrackableItems:updateSuccess'));
      },
      onError: (error: any) => {
        LayoutStoreInstance.setError(
          i18n.t('TrackableItems:updateError') + ': ' + error.message,
        );
      },
    });

  const {
    data: trackableItemData,
    error: trackableItemError,
    refetch: validateTrackableItem,
  } = useTrackableItemQuery({
    skip: true,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (trackableItemData) {
      setValidatedItem(trackableItemData.trackableItem);
      setValidatingItem(false);
      setShowConfirmAddModal(true);
    }
  }, [trackableItemData]);

  useEffect(() => {
    if (trackableItemError) {
      setValidatingItem(false);
      LayoutStoreInstance.setError(
        i18n.t('TrackableItems:validationError') +
          ': ' +
          trackableItemError.message,
      );
    }
  }, [trackableItemError]);

  const handleValidateItem = async () => {
    if (!newItemId.trim()) return;

    setValidatingItem(true);
    await validateTrackableItem({
      id: newItemId.trim(),
    });
  };

  const handleAddItem = async () => {
    if (!validatedItem) return;

    const currentAssetIds = trackableItems.map((item) => item.id);
    const newAssetIds = [...currentAssetIds, validatedItem.id];

    await updateOrderFulfillment({
      variables: {
        order_fulfillment_id: orderFulfillmentId,
        asset_ids: newAssetIds,
      },
    });

    setShowAddModal(false);
    setShowConfirmAddModal(false);
    setNewItemId('');
    setValidatedItem(null);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    const currentAssetIds = trackableItems.map((item) => item.id);
    const newAssetIds = currentAssetIds.filter((id) => id !== itemToDelete.id);

    await updateOrderFulfillment({
      variables: {
        order_fulfillment_id: orderFulfillmentId,
        asset_ids: newAssetIds,
      },
    });

    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const resetAddModal = () => {
    setShowAddModal(false);
    setShowConfirmAddModal(false);
    setNewItemId('');
    setValidatedItem(null);
  };

  return (
    <div>
      {allowAdd && (
        <div className="d-flex justify-content-end mb-3">
          <Button color="primary" onClick={() => setShowAddModal(true)}>
            <FaPlus className="me-1" />
            {i18n.t('TrackableItems:addItem')}
          </Button>
        </div>
      )}

      <Table striped responsive>
        <thead>
          <tr>
            <th>{i18n.t('TrackableItems:name')}</th>
            <th>{i18n.t('TrackableItems:itemType')}</th>
            {allowDelete && <th>{i18n.t('Common:actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {trackableItems.length === 0 ? (
            <tr>
              <td colSpan={allowDelete ? 3 : 2} className="text-center">
                {i18n.t('TrackableItems:noItems')}
              </td>
            </tr>
          ) : (
            trackableItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <TrackableItemDisplay
                    trackableItem={item as TrackableItem}
                    showTrackableItemId={false}
                    size={'s'}
                  />
                </td>
                <td>{item.item_type.name}</td>
                {allowDelete && (
                  <td>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => {
                        setItemToDelete(item);
                        setShowDeleteModal(true);
                      }}
                      disabled={updatingOrderFulfillment}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Add Item Modal */}
      <Modal isOpen={showAddModal} toggle={resetAddModal} size="lg">
        <ModalHeader toggle={resetAddModal}>
          {i18n.t('TrackableItems:addItemTitle')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">
              {i18n.t('TrackableItems:itemId')}
            </label>
            <div className="input-group">
              <Input
                type="text"
                value={newItemId}
                onChange={(e) => setNewItemId(e.target.value)}
                placeholder={i18n.t('TrackableItems:enterItemId')}
                disabled={validatingItem}
              />
              <Button
                color="primary"
                onClick={handleValidateItem}
                disabled={validatingItem || !newItemId.trim()}
              >
                {validatingItem ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <FaCheck className="me-1" />
                    {i18n.t('TrackableItems:validate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={resetAddModal}>
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm Add Item Modal */}
      <Modal
        isOpen={showConfirmAddModal}
        toggle={() => setShowConfirmAddModal(false)}
      >
        <ModalHeader toggle={() => setShowConfirmAddModal(false)}>
          {i18n.t('TrackableItems:confirmAddTitle')}
        </ModalHeader>
        <ModalBody>
          {validatedItem && (
            <div>
              <p>{i18n.t('TrackableItems:confirmAddMessage')}</p>
              <div className="rounded border p-3">
                <div className="mb-2">
                  <TrackableItemDisplay
                    trackableItem={validatedItem as TrackableItem}
                    showName={false}
                  />
                </div>
                <p>
                  <strong>{i18n.t('TrackableItems:name')}:</strong>{' '}
                  {validatedItem.name}
                </p>
                <p>
                  <strong>{i18n.t('Common:id')}:</strong> {validatedItem.id}
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleAddItem}
            disabled={updatingOrderFulfillment}
          >
            {updatingOrderFulfillment ? (
              <ClassicLoadingSpinner />
            ) : (
              <>
                <FaCheck className="me-1" />
                {i18n.t('TrackableItems:confirmAdd')}
              </>
            )}
          </Button>
          <Button
            color="secondary"
            onClick={() => setShowConfirmAddModal(false)}
          >
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} toggle={() => setShowDeleteModal(false)}>
        <ModalHeader toggle={() => setShowDeleteModal(false)}>
          {i18n.t('TrackableItems:confirmDeleteTitle')}
        </ModalHeader>
        <ModalBody>
          {itemToDelete && (
            <div>
              <p>{i18n.t('TrackableItems:confirmDeleteMessage')}</p>
              <div className="rounded border p-3">
                <div className="mb-2">
                  <TrackableItemDisplay
                    trackableItem={itemToDelete as TrackableItem}
                  />
                </div>
                <p>
                  <strong>{i18n.t('TrackableItems:name')}:</strong>{' '}
                  {itemToDelete.name}
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={handleDeleteItem}
            disabled={updatingOrderFulfillment}
          >
            {updatingOrderFulfillment ? (
              <ClassicLoadingSpinner />
            ) : (
              <>
                <FaTrash className="me-1" />
                {i18n.t('TrackableItems:confirmDelete')}
              </>
            )}
          </Button>
          <Button color="secondary" onClick={() => setShowDeleteModal(false)}>
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TrackableItemsTable;
